/**
 * Adaptive render resolution (P10.13). Measured: drawing pixels, not game logic, limits slow
 * devices (a phone-sized match under 6× CPU throttling ran at 35 fps at 2× pixel density and
 * at 60 fps at 1×). In "auto" mode the game starts sharp and, if frames stay slow for a few
 * seconds, drops to 1× density for the rest of the session. It never switches back, so the
 * picture doesn't flip between sharp and soft.
 */

/** auto = sharp until the device struggles; sharp = always full density; standard = always 1×. */
export type ResolutionMode = "auto" | "sharp" | "standard";

/** Frames per measuring window (~2 s at 60 fps). */
const WINDOW_FRAMES = 120;
/** A window averaging longer frames than this (under ~45 fps) is "slow". */
const SLOW_FRAME_MS = 1000 / 45;
/** Consecutive slow windows before lowering the resolution. */
const SLOW_WINDOWS = 2;
/** Longer gaps are tab switches or loading hitches, not rendering cost. */
const MAX_COUNTED_FRAME_MS = 250;
/** Frames ignored at the start (asset upload, shader compilation). */
const WARMUP_FRAMES = 120;

/** Decides, from frame times, when auto mode should give up on high density. Pure. */
export class ResolutionGovernor {
	private seen = 0;
	private frames = 0;
	private total = 0;
	private slowWindows = 0;
	private decided = false;

	/** Feeds one frame's duration; returns true exactly once, when it is time to lower. */
	frame(deltaMs: number): boolean {
		if (this.decided) return false;
		this.seen++;
		if (this.seen <= WARMUP_FRAMES || deltaMs > MAX_COUNTED_FRAME_MS || deltaMs <= 0) return false;
		this.frames++;
		this.total += deltaMs;
		if (this.frames < WINDOW_FRAMES) return false;
		const slow = this.total / this.frames > SLOW_FRAME_MS;
		this.frames = 0;
		this.total = 0;
		this.slowWindows = slow ? this.slowWindows + 1 : 0;
		if (this.slowWindows < SLOW_WINDOWS) return false;
		this.decided = true;
		return true;
	}

	/** Starts over (e.g. the player picked Auto again). */
	reset(): void {
		this.seen = 0;
		this.frames = 0;
		this.total = 0;
		this.slowWindows = 0;
		this.decided = false;
	}
}
